import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/League.css";
import "../css/Leaderboard.css";
import "../css/Welcome.css";
import useLeague from "../hooks/useLeague";
import { useFriends } from "../hooks/useFriends";
import { useUser } from "../hooks/useUser";
import useSeason from "../hooks/useSeason";
import useEventNames from "../hooks/useEventNames";
import Leaderboard from "./Leaderboard";
import SearchSuggestions from "./SearchSuggestions";

const League = () => {
  const {
    fetchCurrentUserLeague,
    createLeague,
    currentLeague,
    loading,
    initialLoading,
    error,
    inviteFriendToLeague,
    toggleIsJoinable,
    toggleShowInactiveMembers,
    resetLeagueInviteCode,
    leaveLeague,
    deleteLeague,
    kickMember,
    banMember,
    unbanMember,
    transferOwnership,
    updateLeague,
    setSeasonalRewards,
    setEventRewards,
    getLeagueRewards,
  } = useLeague();
  const navigate = useNavigate();
  const { getFriends } = useFriends();
  const { user } = useUser();
  const { seasons, fetchSeasons, currentSeason, fetchCurrentSeason } =
    useSeason();
  const { fetchEventNames, eventNames } = useEventNames();
  const [leagueName, setLeagueName] = useState("");
  const [leagueDescription, setLeagueDescription] = useState("");
  const [editLeagueName, setEditLeagueName] = useState("");
  const [editLeagueDescription, setEditLeagueDescription] = useState("");
  const [isOwnerView, setIsOwnerView] = useState(false);
  const [friends, setFriends] = useState([]);
  const [overlay, setOverlay] = useState("None");
  const [invitingFriends, setInvitingFriends] = useState({}); // Track loading state per friend
  const [friendErrors, setFriendErrors] = useState({}); // Track error state per friend
  const [kickLoading, setKickLoading] = useState({}); // Track loading state for kicks
  const [kickErrors, setKickErrors] = useState({}); // Track error state for kicks
  const [banLoading, setBanLoading] = useState({}); // Track loading state for bans
  const [banErrors, setBanErrors] = useState({}); // Track error state for bans
  const [unbanLoading, setUnbanLoading] = useState({}); // Track loading state for unbans
  const [unbanErrors, setUnbanErrors] = useState({}); // Track error state for unbans
  const [transferLoading, setTransferLoading] = useState({}); // Track loading state for transfers
  const [transferErrors, setTransferErrors] = useState({}); // Track error state for transfers
  const [memberSearchTerm, setMemberSearchTerm] = useState(""); // Track search term for filtering members
  const [manageView, setManageView] = useState("Members");
  const [confirmingAction, setConfirmingAction] = useState(null); // Track which action is awaiting confirmation

  // Rewards management state
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [seasonalRewards, setSeasonalRewardsState] = useState([
    { position: 1, reward: "" },
  ]);
  const [eventRewards, setEventRewardsState] = useState([
    { position: 1, reward: "" },
  ]);
  const [rewardsView, setRewardsView] = useState("seasonal"); // 'seasonal' or 'event'
  const [showInactiveMembers, setShowInactiveMembers] = useState(true); // Track whether to show inactive members in leaderboard

  // Dropdown state for click-based dropdowns
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  useEffect(() => {
    if (currentLeague && user) {
      setIsOwnerView(currentLeague.owner._id === user._id);
      setShowInactiveMembers(
        currentLeague.showInactiveMembers !== undefined
          ? currentLeague.showInactiveMembers
          : true,
      );
    }
    const fetchFriends = async () => {
      const friendsList = await getFriends();
      setFriends(friendsList);
    };
    fetchFriends();

    // Fetch seasons for rewards management
    fetchSeasons();
    fetchCurrentSeason();
  }, [currentLeague, user]);

  // Fetch events when season is selected
  useEffect(() => {
    if (selectedSeason && overlay === "ManageRewards") {
      fetchEventNames(selectedSeason._id);
    }
  }, [selectedSeason, overlay]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown
      if (!event.target.closest(".dropdown.click")) {
        setSeasonDropdownOpen(false);
        setEventDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Rewards management functions
  const handleAddReward = (type) => {
    if (type === "seasonal") {
      setSeasonalRewardsState((prev) => [
        ...prev,
        { position: prev.length + 1, reward: "" },
      ]);
    } else {
      setEventRewardsState((prev) => [
        ...prev,
        { position: prev.length + 1, reward: "" },
      ]);
    }
  };

  const handleRemoveReward = (type, index) => {
    if (type === "seasonal") {
      setSeasonalRewardsState((prev) => prev.filter((_, i) => i !== index));
    } else {
      setEventRewardsState((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleRewardChange = (type, index, field, value) => {
    if (type === "seasonal") {
      setSeasonalRewardsState((prev) =>
        prev.map((reward, i) =>
          i === index ? { ...reward, [field]: value } : reward,
        ),
      );
    } else {
      setEventRewardsState((prev) =>
        prev.map((reward, i) =>
          i === index ? { ...reward, [field]: value } : reward,
        ),
      );
    }
  };

  const handleSaveRewards = async (type) => {
    if (!currentLeague || !selectedSeason) return;

    try {
      if (type === "seasonal") {
        const validRewards = seasonalRewards.filter(
          (r) => r.reward.trim() !== "",
        );
        const result = await setSeasonalRewards(
          currentLeague._id,
          selectedSeason._id,
          validRewards,
        );
        if (result.success) {
          console.log("Seasonal rewards saved successfully");
        }
      } else if (type === "event" && selectedEvent) {
        const validRewards = eventRewards.filter((r) => r.reward.trim() !== "");
        const result = await setEventRewards(
          currentLeague._id,
          selectedSeason._id,
          selectedEvent._id,
          validRewards,
        );
        if (result.success) {
          console.log("Event rewards saved successfully");
        }
      }
      setOverlay("None");
    } catch (error) {
      console.error("Error saving rewards:", error);
    }
  };

  const loadExistingRewards = async () => {
    if (!currentLeague || !selectedSeason) return;

    try {
      const result = await getLeagueRewards(
        currentLeague._id,
        selectedSeason._id,
      );
      if (result.success) {
        setSeasonalRewardsState(
          result.seasonalRewards.length > 0
            ? result.seasonalRewards
            : [{ position: 1, reward: "" }],
        );

        // Load event rewards if an event is selected
        if (selectedEvent) {
          const eventReward = result.eventRewards.find(
            (er) => er.event._id === selectedEvent._id,
          );
          setEventRewardsState(
            eventReward?.rewards.length > 0
              ? eventReward.rewards
              : [{ position: 1, reward: "" }],
          );
        }
      }
    } catch (error) {
      console.error("Error loading existing rewards:", error);
    }
  };

  // Load existing rewards when season or event changes
  useEffect(() => {
    if (overlay === "ManageRewards" && selectedSeason) {
      loadExistingRewards();
    }
  }, [selectedSeason, selectedEvent, overlay]);

  const handleCreateLeague = (e) => {
    e.preventDefault();
    createLeague({ name: leagueName, description: leagueDescription });
  };

  const handleOverlayChange = (newOverlay) => {
    if (newOverlay === "EditLeague" && currentLeague) {
      setEditLeagueName(currentLeague.name);
      setEditLeagueDescription(currentLeague.description);
      setShowInactiveMembers(
        currentLeague.showInactiveMembers !== undefined
          ? currentLeague.showInactiveMembers
          : true,
      );
    }
    setOverlay(newOverlay);
  };

  const handleOverlayClose = () => {
    setOverlay("None");
    setManageView("Members");
    setMemberSearchTerm(""); // Clear search term when closing overlay
    setConfirmingAction(null); // Reset confirmation state when closing overlay
  };

  const handleInviteFriend = async (friendId) => {
    if (!currentLeague) return;

    // Set loading state for this specific friend
    setInvitingFriends((prev) => {
      const newState = { ...prev, [friendId]: true };
      return newState;
    });
    // Clear any previous error for this friend
    setFriendErrors((prev) => ({ ...prev, [friendId]: null }));

    try {
      const result = await inviteFriendToLeague(currentLeague._id, friendId);
      if (result && result.success) {
        console.log("Friend invited successfully:", result.message);
        // Clear any previous error on success
        setFriendErrors((prev) => ({ ...prev, [friendId]: null }));
        // Refresh league data to get updated invited users list
        await fetchCurrentUserLeague();
      } else {
        // Set error state for this specific friend
        const errorMessage =
          (result && result.error) || "Failed to invite friend";
        setFriendErrors((prev) => ({ ...prev, [friendId]: errorMessage }));
      }
    } catch (error) {
      console.error("Error inviting friend:", error);
      // Set error state for this specific friend
      setFriendErrors((prev) => ({
        ...prev,
        [friendId]: error.message || "Failed to invite friend",
      }));
    } finally {
      // Clear loading state for this specific friend
      setInvitingFriends((prev) => {
        const newState = { ...prev, [friendId]: false };
        console.log("Clearing loading state:", newState);
        return newState;
      });
    }
  };

  const handleToggleJoinable = async () => {
    if (!currentLeague || !isOwnerView) return;

    try {
      const result = await toggleIsJoinable(currentLeague._id);
      if (result && result.success) {
        console.log("League joinable status toggled:", result.message);
        // The useLeague hook should automatically update the currentLeague state
      }
    } catch (error) {
      console.error("Error toggling joinable status:", error);
    }
  };

  const handleToggleShowInactiveMembers = async () => {
    if (!currentLeague || !isOwnerView) return;

    try {
      const result = await toggleShowInactiveMembers(currentLeague._id);
      if (result && result.success) {
        console.log(
          "League show inactive members status toggled:",
          result.message,
        );
        // The useLeague hook should automatically update the currentLeague state
        setShowInactiveMembers(result.showInactiveMembers);
      }
    } catch (error) {
      console.error("Error toggling show inactive members status:", error);
    }
  };

  const handleResetInviteCode = async () => {
    if (!currentLeague || !isOwnerView) return;

    try {
      const result = await resetLeagueInviteCode(currentLeague._id);
      if (result && result.success) {
        console.log("Invite code reset:", result.message);
        // Refresh league data to get the new invite code
        await fetchCurrentUserLeague();
      }
    } catch (error) {
      console.error("Error resetting invite code:", error);
    }
  };

  // Helper function to handle confirmation logic
  const handleActionWithConfirmation = (actionType, targetId, callback) => {
    const actionKey = `${actionType}_${targetId}`;

    if (confirmingAction === actionKey) {
      // Second click - execute the action
      setConfirmingAction(null);
      callback();
    } else {
      // First click - set confirmation state
      setConfirmingAction(actionKey);
    }
  };

  const handleKickMember = async (memberId) => {
    if (!currentLeague || !isOwnerView) return;

    const executeKick = async () => {
      // Set loading state for this specific member
      setKickLoading((prev) => ({ ...prev, [memberId]: true }));
      // Clear any previous error for this member
      setKickErrors((prev) => ({ ...prev, [memberId]: null }));

      try {
        const result = await kickMember(currentLeague._id, memberId);
        if (result && result.success) {
          console.log("Member kicked successfully:", result.message);
          // Clear any previous error on success
          setKickErrors((prev) => ({ ...prev, [memberId]: null }));
          // Refresh league data to get updated members list
          await fetchCurrentUserLeague();
        } else {
          // Set error state for this specific member
          const errorMessage =
            (result && result.error) || "Failed to kick member";
          setKickErrors((prev) => ({ ...prev, [memberId]: errorMessage }));
        }
      } catch (error) {
        console.error("Error kicking member:", error);
        // Set error state for this specific member
        setKickErrors((prev) => ({
          ...prev,
          [memberId]: error.message || "Failed to kick member",
        }));
      } finally {
        // Clear loading state for this specific member
        setKickLoading((prev) => ({ ...prev, [memberId]: false }));
      }
    };

    handleActionWithConfirmation("kick", memberId, executeKick);
  };

  const handleBanMember = async (memberId, reason = "") => {
    if (!currentLeague || !isOwnerView) return;

    const executeBan = async () => {
      // Set loading state for this specific member
      setBanLoading((prev) => ({ ...prev, [memberId]: true }));
      // Clear any previous error for this member
      setBanErrors((prev) => ({ ...prev, [memberId]: null }));

      try {
        const result = await banMember(currentLeague._id, memberId, reason);
        if (result && result.success) {
          console.log("Member banned successfully:", result.message);
          // Clear any previous error on success
          setBanErrors((prev) => ({ ...prev, [memberId]: null }));
          // Refresh league data to get updated members list
          await fetchCurrentUserLeague();
        } else {
          // Set error state for this specific member
          const errorMessage =
            (result && result.error) || "Failed to ban member";
          setBanErrors((prev) => ({ ...prev, [memberId]: errorMessage }));
        }
      } catch (error) {
        console.error("Error banning member:", error);
        // Set error state for this specific member
        setBanErrors((prev) => ({
          ...prev,
          [memberId]: error.message || "Failed to ban member",
        }));
      } finally {
        // Clear loading state for this specific member
        setBanLoading((prev) => ({ ...prev, [memberId]: false }));
      }
    };

    handleActionWithConfirmation("ban", memberId, executeBan);
  };

  const handleUnbanMember = async (userId) => {
    if (!currentLeague || !isOwnerView) return;

    const executeUnban = async () => {
      // Set loading state for this specific user
      setUnbanLoading((prev) => ({ ...prev, [userId]: true }));
      // Clear any previous error for this user
      setUnbanErrors((prev) => ({ ...prev, [userId]: null }));

      try {
        const result = await unbanMember(currentLeague._id, userId);
        if (result && result.success) {
          console.log("Member unbanned successfully:", result.message);
          // Clear any previous error on success
          setUnbanErrors((prev) => ({ ...prev, [userId]: null }));
          // Refresh league data to get updated banned users list
          await fetchCurrentUserLeague();
        } else {
          // Set error state for this specific user
          const errorMessage =
            (result && result.error) || "Failed to unban member";
          setUnbanErrors((prev) => ({ ...prev, [userId]: errorMessage }));
        }
      } catch (error) {
        console.error("Error unbanning member:", error);
        // Set error state for this specific user
        setUnbanErrors((prev) => ({
          ...prev,
          [userId]: error.message || "Failed to unban member",
        }));
      } finally {
        // Clear loading state for this specific user
        setUnbanLoading((prev) => ({ ...prev, [userId]: false }));
      }
    };

    handleActionWithConfirmation("unban", userId, executeUnban);
  };

  const handleTransferOwnership = async (newOwnerId) => {
    if (!currentLeague || !isOwnerView) return;

    const executeTransfer = async () => {
      // Set loading state for this specific member
      setTransferLoading((prev) => ({ ...prev, [newOwnerId]: true }));
      // Clear any previous error for this member
      setTransferErrors((prev) => ({ ...prev, [newOwnerId]: null }));

      try {
        const result = await transferOwnership(currentLeague._id, newOwnerId);
        if (result && result.success) {
          console.log("Ownership transferred successfully:", result.message);
          // Clear any previous error on success
          setTransferErrors((prev) => ({ ...prev, [newOwnerId]: null }));
          // Refresh league data to get updated ownership
          setOverlay("None");
          await fetchCurrentUserLeague();
        } else {
          // Set error state for this specific member
          const errorMessage =
            (result && result.error) || "Failed to transfer ownership";
          setTransferErrors((prev) => ({
            ...prev,
            [newOwnerId]: errorMessage,
          }));
        }
      } catch (error) {
        console.error("Error transferring ownership:", error);
        // Set error state for this specific member
        setTransferErrors((prev) => ({
          ...prev,
          [newOwnerId]: error.message || "Failed to transfer ownership",
        }));
      } finally {
        // Clear loading state for this specific member
        setTransferLoading((prev) => ({ ...prev, [newOwnerId]: false }));
      }
    };

    handleActionWithConfirmation("transfer", newOwnerId, executeTransfer);
  };

  const handleEditLeague = async (e) => {
    e.preventDefault();
    if (!currentLeague || !isOwnerView) return;

    try {
      const result = await updateLeague(currentLeague._id, {
        name: editLeagueName,
        description: editLeagueDescription,
      });
      if (result && result.success) {
        // Refresh league data
        setOverlay("None");
        await fetchCurrentUserLeague();
      }
    } catch (error) {
      console.error("Error updating league:", error);
    }
  };

  // Dropdown handlers for click-based dropdowns
  const handleSeasonDropdownToggle = () => {
    setSeasonDropdownOpen(!seasonDropdownOpen);
    setEventDropdownOpen(false); // Close other dropdown
  };

  const handleEventDropdownToggle = () => {
    setEventDropdownOpen(!eventDropdownOpen);
    setSeasonDropdownOpen(false); // Close other dropdown
  };

  const handleSeasonSelect = (season) => {
    setSelectedSeason(season);
    setSeasonDropdownOpen(false);
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setEventDropdownOpen(false);
  };

  // Show loading state while fetching initial league data
  if (initialLoading) {
    return (
      <div className="main-container col-10 offset-1">
        <div
          className="title-container"
          style={{ marginBottom: "0.5vw", position: "relative" }}
        >
          <div className="col-10 inventory-box">
            <div className="col-auto">
              <h1 className="title">League</h1>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  const handleLeaveConfirm = async () => {
    if (!currentLeague || isOwnerView) return;

    try {
      const result = await leaveLeague();
      if (result && result.success) {
        // Refresh league data
        setOverlay("None");
        await fetchCurrentUserLeague();
      }
    } catch (error) {
      console.error("Error leaving league:", error);
    }
  };

  const handleDeleteLeague = async () => {
    if (!currentLeague || !isOwnerView) return;

    try {
      const result = await deleteLeague(currentLeague._id);
      if (result && result.success) {
        // Refresh league data
        setOverlay("None");
        await fetchCurrentUserLeague();
      }
    } catch (error) {
      console.error("Error deleting league:", error);
    }
  };

  return (
    <div className="main-container col-10 offset-1">
      <div className="scroll-container">
        <div
          className="title-container"
          style={{ marginBottom: "0.5vw", position: "relative" }}
        >
          <div className=" inventory-box">
            <div className="col-auto">
              <h1 className="title">
                {currentLeague ? currentLeague.name : "League"}
              </h1>
            </div>
          </div>
          {currentLeague &&
            (!isOwnerView ? (
              <div className="profile-options">
                <button
                  className="default-button"
                  style={{ marginRight: "0.5vw" }}
                  {...(!currentLeague.isJoinable && { disabled: true })}
                  onClick={() => handleOverlayChange("Invite")}
                >
                  Invite
                </button>
                <button
                  className="default-button"
                  onClick={() => handleOverlayChange("Leave")}
                >
                  Leave
                </button>
              </div>
            ) : (
              <div className="profile-options">
                <button
                  className="default-button"
                  style={{ marginRight: "0.5vw" }}
                  onClick={() => handleOverlayChange("Invite")}
                >
                  Invite
                </button>
                <button
                  className="default-button"
                  style={{ marginRight: "0.5vw" }}
                  onClick={() => handleOverlayChange("ManageMembers")}
                >
                  Manage Members
                </button>
                <button
                  className="default-button"
                  style={{ marginRight: "0.5vw" }}
                  onClick={() => handleOverlayChange("ManageRewards")}
                >
                  Manage Rewards
                </button>
                <button
                  className="default-button"
                  style={{ marginRight: "0.5vw" }}
                  onClick={() => handleOverlayChange("EditLeague")}
                >
                  Edit League
                </button>
                <button
                  className="default-button"
                  onClick={() => handleOverlayChange("DeleteLeague")}
                >
                  Delete League
                </button>
              </div>
            ))}
        </div>

        {currentLeague ? (
          <>
            <div className="event-page-container">
              <h1 className="title" style={{ fontSize: "1.5vw" }}>
                Information
              </h1>
              <div className="eventpage-desc">
                <div
                  className="eventpage-desc-table-container"
                  style={{ marginBottom: "0" }}
                >
                  <div className="profile-stats-table-container">
                    <table
                      className="profile-stats-table"
                      style={{ height: "100%" }}
                    >
                      <tbody>
                        <tr>
                          <td>
                            Description:{" "}
                            <span
                              style={{
                                fontWeight: "normal",
                                fontStyle: "italic",
                              }}
                            >
                              {currentLeague.description}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="profile-stats-table-container">
                    <table
                      className="profile-stats-table"
                      style={{ height: "100%" }}
                    >
                      <tbody>
                        <tr>
                          <td>Owner: </td>
                          <td>{currentLeague.owner.username}</td>
                        </tr>
                        <tr>
                          <td>Created: </td>
                          <td>
                            {new Date(
                              currentLeague.createdAt,
                            ).toLocaleDateString()}
                          </td>
                        </tr>
                        <tr>
                          <td>Members: </td>
                          <td>{currentLeague.members.length}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Leaderboard league={currentLeague} fullSize={true} />
            </div>
          </>
        ) : (
          <div className="league-creation-main-container">
            <div
              className="league-creation-container"
              style={{ textAlign: "center" }}
            >
              <h4>You're currently not in a league.</h4>

              <p>
                Leagues are custom groups where you, your friends, colleagues,
                or fans can compete on a private leaderboard, set custom rewards
                that matter to you, and track who’s the best strategist in the
                group.
              </p>

              <p>
                You can create your own league and invite others to join, or
                join an existing one via invite or shareable link. More info can
                be found under 'Leagues' in the 'How to Play' section.
              </p>
            </div>

            <div className="league-creation-container">
              <h1 className="title-welcome">Create League</h1>
              <form style={{ width: "70%" }} onSubmit={handleCreateLeague}>
                <label className="login-label" htmlFor="leagueName">
                  League Name:
                </label>
                <input
                  className="input-field"
                  type="text"
                  id="leagueName"
                  name="leagueName"
                  placeholder="Enter your League Name"
                  required
                  onChange={(e) => setLeagueName(e.target.value)}
                  value={leagueName}
                  maxLength={50}
                />

                <label className="login-label" htmlFor="leagueDescription">
                  League Description:
                </label>
                <textarea
                  className="input-field"
                  id="leagueDescription"
                  name="leagueDescription"
                  placeholder="Optionally enter a description for your league (250 characters max)"
                  rows="3"
                  maxLength="250"
                  onChange={(e) => setLeagueDescription(e.target.value)}
                  value={leagueDescription}
                />

                <div className="wrap">
                  <button
                    className="default-button submit-button"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? <div className="loader-small"></div> : "Create"}
                  </button>
                </div>
                <p style={{ height: "1.5vw" }}>
                  {error && (
                    <div className="error" style={{ margin: 0 }}>
                      {error}
                    </div>
                  )}
                </p>
              </form>
            </div>
          </div>
        )}
      </div>

      {overlay === "Invite" && (
        <div className="overlay" onClick={handleOverlayClose}>
          <div
            className="card-menu-container col-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-box">
              <h1 className="title">Invite</h1>
              {isOwnerView && (
                <div className="league-toggle-container">
                  <span className="league-toggle-label">
                    Allow new members:
                  </span>
                  <label className="league-toggle-switch">
                    <input
                      type="checkbox"
                      checked={currentLeague.isJoinable}
                      onChange={handleToggleJoinable}
                    />
                    <span className="league-toggle-slider"></span>
                  </label>
                </div>
              )}
            </div>

            <button className="overlay-close" onClick={handleOverlayClose}>
              X
            </button>
            <div className="card-menu-content-container">
              <div
                className="card-menu-content"
                style={{ flexDirection: "column", padding: "0.5vw" }}
              >
                <div className="scroll-container" style={{ gap: "0.5vw" }}>
                  {friends.map((friend) => {
                    const isLoading = invitingFriends[friend.friend._id];
                    const hasError = friendErrors[friend.friend._id];
                    const isMember = currentLeague?.members.some(
                      (member) => member._id === friend.friend._id,
                    );
                    const isInvited =
                      currentLeague?.invitedUsers &&
                      currentLeague?.invitedUsers.some(
                        (invitation) =>
                          invitation.user._id === friend.friend._id,
                      );

                    return (
                      <div
                        key={friend.friend._id}
                        className="league-friend-container"
                      >
                        <p
                          className="username-clickable"
                          onClick={() =>
                            navigate(`/profile/${friend.friend.userNumber}`)
                          }
                        >
                          {friend.friend.username}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          {hasError && (
                            <span
                              className="error"
                              style={{ marginTop: "0", marginRight: "0.5vw" }}
                            >
                              {hasError}
                            </span>
                          )}
                          <button
                            className="default-button"
                            style={{
                              height: "2.5vw",
                              width: "4vw",
                              alignItems: "center",
                            }}
                            onClick={() =>
                              handleInviteFriend(friend.friend._id)
                            }
                            disabled={isLoading || isMember || isInvited}
                          >
                            {isLoading ? (
                              <div className="loader-small"></div>
                            ) : isMember ? (
                              "Member"
                            ) : isInvited ? (
                              "Invited"
                            ) : (
                              "Invite"
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div
                className="card-menu-content"
                style={{
                  flexDirection: "column",
                  padding: "0.5vw",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <h4 className="title">Invite with link</h4>
                <div className="league-invite-link-container">
                  <input
                    type="text"
                    value={`${window.location.origin}/league/join/${currentLeague?.inviteCode}`}
                    readOnly
                    className="league-invite-input"
                    onClick={(e) => {
                      e.target.select();
                      navigator.clipboard.writeText(e.target.value);
                    }}
                  />
                  {isOwnerView && (
                    <button
                      className="default-button"
                      style={{ width: "4vw" }}
                      onClick={handleResetInviteCode}
                      disabled={loading}
                    >
                      {loading ? <div className="loader-small"></div> : "New"}
                    </button>
                  )}
                </div>
                <button
                  className="default-button"
                  onClick={() => {
                    const link = `${window.location.origin}/league/join/${currentLeague?.inviteCode}`;
                    navigator.clipboard.writeText(link);
                  }}
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {overlay === "ManageMembers" && (
        <div className="overlay" onClick={handleOverlayClose}>
          <div
            className="card-menu-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-box">
              <h1 className="title">Manage Members</h1>
              <div
                className="view-button-container"
                style={{ marginRight: "0.5vw" }}
              >
                <button
                  className={`view-button ${manageView === "Members" ? "active" : ""}`}
                  onClick={() => setManageView("Members")}
                >
                  Members
                </button>
                <button
                  className={`view-button ${manageView === "Banned" ? "active" : ""}`}
                  onClick={() => setManageView("Banned")}
                >
                  Banned Users
                </button>
              </div>
              <SearchSuggestions
                query={
                  manageView === "Members"
                    ? currentLeague?.members || []
                    : currentLeague?.bannedUsers?.map(
                        (banned) => banned.user,
                      ) || []
                }
                searchTerm={memberSearchTerm}
                setSearchTerm={setMemberSearchTerm}
                suggestionsLength={0}
                placeholder={
                  manageView === "Members"
                    ? "Search members..."
                    : "Search banned users..."
                }
              />
            </div>

            <button className="overlay-close" onClick={handleOverlayClose}>
              X
            </button>
            <div className="card-menu-content-container">
              <div
                className="card-menu-content"
                style={{ flexDirection: "column", padding: "0.5vw" }}
              >
                <div className="scroll-container" style={{ gap: "0.5vw" }}>
                  {manageView === "Members" &&
                    currentLeague?.members
                      .filter(
                        (member) =>
                          member._id !== user._id && // Exclude current user from member list
                          member.username
                            .toLowerCase()
                            .includes(memberSearchTerm.toLowerCase()),
                      )
                      .map((member) => {
                        const isKickLoading = kickLoading[member._id];
                        const hasKickError = kickErrors[member._id];
                        const isBanLoading = banLoading[member._id];
                        const hasBanError = banErrors[member._id];
                        const isTransferLoading = transferLoading[member._id];
                        const hasTransferError = transferErrors[member._id];

                        return (
                          <div
                            key={member._id}
                            className="league-friend-container"
                          >
                            <p
                              className="username-clickable"
                              onClick={() =>
                                navigate(`/profile/${member.userNumber}`)
                              }
                            >
                              {member.username}
                            </p>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              {(hasKickError ||
                                hasBanError ||
                                hasTransferError) && (
                                <span
                                  className="error"
                                  style={{
                                    marginTop: "0",
                                    marginRight: "0.5vw",
                                  }}
                                >
                                  {hasKickError ||
                                    hasBanError ||
                                    hasTransferError}
                                </span>
                              )}
                              <button
                                className="default-button"
                                style={{
                                  height: "2.5vw",
                                  width: "8vw",
                                  alignItems: "center",
                                  marginRight: "0.5vw",
                                  backgroundColor:
                                    confirmingAction ===
                                    `transfer_${member._id}`
                                      ? "var(--lightred)"
                                      : "",
                                }}
                                onClick={() =>
                                  handleTransferOwnership(member._id)
                                }
                                disabled={
                                  isTransferLoading || member._id === user._id
                                }
                              >
                                {isTransferLoading ? (
                                  <div className="loader-small"></div>
                                ) : confirmingAction ===
                                  `transfer_${member._id}` ? (
                                  "Sure?"
                                ) : (
                                  "Transfer Ownership"
                                )}
                              </button>
                              <button
                                className="default-button"
                                style={{
                                  height: "2.5vw",
                                  width: "4vw",
                                  alignItems: "center",
                                  marginRight: "0.5vw",
                                  backgroundColor:
                                    confirmingAction === `kick_${member._id}`
                                      ? "var(--lightred)"
                                      : "",
                                }}
                                onClick={() => handleKickMember(member._id)}
                                disabled={
                                  isKickLoading || member._id === user._id
                                }
                              >
                                {isKickLoading ? (
                                  <div className="loader-small"></div>
                                ) : confirmingAction ===
                                  `kick_${member._id}` ? (
                                  "Sure?"
                                ) : (
                                  "Kick"
                                )}
                              </button>
                              <button
                                className="default-button"
                                style={{
                                  height: "2.5vw",
                                  width: "4vw",
                                  alignItems: "center",
                                  backgroundColor:
                                    confirmingAction === `ban_${member._id}`
                                      ? "var(--lightred)"
                                      : "",
                                }}
                                onClick={() => handleBanMember(member._id)}
                                disabled={
                                  isBanLoading || member._id === user._id
                                }
                              >
                                {isBanLoading ? (
                                  <div className="loader-small"></div>
                                ) : confirmingAction === `ban_${member._id}` ? (
                                  "Sure?"
                                ) : (
                                  "Ban"
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}

                  {manageView === "Banned" &&
                    currentLeague?.bannedUsers
                      .filter((banned) =>
                        banned.user.username
                          .toLowerCase()
                          .includes(memberSearchTerm.toLowerCase()),
                      )
                      .map((banned) => {
                        const isUnbanLoading = unbanLoading[banned.user._id];
                        const hasUnbanError = unbanErrors[banned.user._id];

                        return (
                          <div
                            key={banned.user._id}
                            className="league-friend-container"
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <p
                                className="username-clickable"
                                style={{ height: "1.5vw" }}
                                onClick={() =>
                                  navigate(`/profile/${banned.user.userNumber}`)
                                }
                              >
                                {banned.user.username}
                              </p>
                              {banned.reason && (
                                <span
                                  style={{
                                    fontSize: "0.8vw",
                                    fontStyle: "italic",
                                    color: "#888",
                                  }}
                                >
                                  Reason: {banned.reason}
                                </span>
                              )}
                              <span
                                style={{ fontSize: "0.8vw", color: "#888" }}
                              >
                                Banned:{" "}
                                {new Date(banned.bannedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              {hasUnbanError && (
                                <span
                                  className="error"
                                  style={{
                                    marginTop: "0",
                                    marginRight: "0.5vw",
                                  }}
                                >
                                  {hasUnbanError}
                                </span>
                              )}
                              <button
                                className="default-button"
                                style={{
                                  height: "2.5vw",
                                  width: "6vw",
                                  alignItems: "center",
                                  backgroundColor:
                                    confirmingAction ===
                                    `unban_${banned.user._id}`
                                      ? "var(--lightred)"
                                      : "",
                                }}
                                onClick={() =>
                                  handleUnbanMember(banned.user._id)
                                }
                                disabled={isUnbanLoading}
                              >
                                {isUnbanLoading ? (
                                  <div className="loader-small"></div>
                                ) : confirmingAction ===
                                  `unban_${banned.user._id}` ? (
                                  "Sure?"
                                ) : (
                                  "Unban"
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {overlay === "Leave" && (
        <div className="overlay" onClick={handleOverlayClose}>
          <div
            className="card-menu-container"
            style={{ minWidth: "30%", minHeight: "25%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-box">
              <h1 className="title">Leave League</h1>
            </div>
            <button className="overlay-close" onClick={handleOverlayClose}>
              X
            </button>

            <div className="card-menu-content" style={{ alignItems: "center" }}>
              <button
                className="default-button big"
                style={{ height: "4vw" }}
                onClick={handleLeaveConfirm}
                disabled={loading}
              >
                {" "}
                {loading ? (
                  <div className="loader-small"></div>
                ) : (
                  "Leave League"
                )}{" "}
              </button>
            </div>
          </div>
        </div>
      )}

      {overlay === "EditLeague" && (
        <div className="overlay" onClick={handleOverlayClose}>
          <div
            className="card-menu-container"
            style={{ minWidth: "40%", minHeight: "50%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-box">
              <h1 className="title">Edit League</h1>
              {isOwnerView && (
                <div className="league-toggle-container">
                  <span className="league-toggle-label">
                    Only show current members in leaderboard:
                  </span>
                  <label className="league-toggle-switch">
                    <input
                      type="checkbox"
                      checked={!showInactiveMembers}
                      onChange={handleToggleShowInactiveMembers}
                    />
                    <span className="league-toggle-slider"></span>
                  </label>
                </div>
              )}
            </div>
            <button className="overlay-close" onClick={handleOverlayClose}>
              X
            </button>

            <div
              className="card-menu-content"
              style={{ flexDirection: "column", padding: "2vw" }}
            >
              <form style={{ width: "100%" }} onSubmit={handleEditLeague}>
                <label className="login-label" htmlFor="editLeagueName">
                  League Name:
                </label>
                <input
                  className="input-field"
                  type="text"
                  id="editLeagueName"
                  name="editLeagueName"
                  placeholder="Enter your League Name"
                  required
                  onChange={(e) => setEditLeagueName(e.target.value)}
                  value={editLeagueName}
                  maxLength={50}
                />

                <label className="login-label" htmlFor="editLeagueDescription">
                  League Description:
                </label>
                <textarea
                  className="input-field"
                  id="editLeagueDescription"
                  name="editLeagueDescription"
                  placeholder="Optionally enter a description for your league (250 characters max)"
                  rows="3"
                  maxLength="250"
                  onChange={(e) => setEditLeagueDescription(e.target.value)}
                  value={editLeagueDescription}
                />

                <div className="wrap" style={{ marginTop: "2vw" }}>
                  <button
                    className="default-button submit-button"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="loader-small"></div>
                    ) : (
                      "Update League"
                    )}
                  </button>
                </div>
                {error && (
                  <p>
                    <div className="error">{error}</div>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {overlay === "ManageRewards" && (
        <div className="overlay" onClick={handleOverlayClose}>
          <div
            className="card-menu-container"
            style={{ width: "60%", height: "70%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-box">
              <h1 className="title">Manage Rewards</h1>
            </div>
            <button className="overlay-close" onClick={handleOverlayClose}>
              X
            </button>
            <div
              className="card-menu-content"
              style={{
                flexDirection: "column",
                justifyContent: "flex-start",
                overflowY: "auto",
              }}
            >
              {/* Season Selection */}
              <div style={{ marginBottom: "0.5vw" }}>
                <label className="login-label">Select Season:</label>
                <div
                  className={`dropdown click ${seasonDropdownOpen ? "open" : ""}`}
                  style={{ width: "100%" }}
                >
                  <button
                    className="dropdown-button"
                    style={{ width: "100%" }}
                    onClick={handleSeasonDropdownToggle}
                  >
                    {selectedSeason ? selectedSeason.name : "Select Season"}
                  </button>
                  <div
                    className="dropdown-content scroll"
                    style={{ width: "100%" }}
                  >
                    {seasons &&
                      seasons.map((season, index) => (
                        <p
                          key={index}
                          onClick={() => handleSeasonSelect(season)}
                        >
                          {season.name}
                        </p>
                      ))}
                  </div>
                </div>
              </div>

              {selectedSeason && (
                <>
                  {/* Rewards Type Toggle */}
                  <div style={{ marginBottom: "0.5vw" }}>
                    <div
                      className="view-button-container"
                      style={{ margin: "0" }}
                    >
                      <button
                        className={`view-button ${rewardsView === "seasonal" ? "active" : ""}`}
                        onClick={() => setRewardsView("seasonal")}
                      >
                        Season Rewards
                      </button>
                      <button
                        className={`view-button ${rewardsView === "event" ? "active" : ""}`}
                        onClick={() => setRewardsView("event")}
                      >
                        Event Rewards
                      </button>
                    </div>
                  </div>

                  {/* Event Selection (only shown for event rewards) */}
                  {rewardsView === "event" && (
                    <div style={{ marginBottom: "1vw" }}>
                      <label className="login-label">Select Event:</label>
                      <div
                        className={`dropdown click ${eventDropdownOpen ? "open" : ""}`}
                        style={{ width: "100%" }}
                      >
                        <button
                          className="dropdown-button"
                          style={{ width: "100%" }}
                          onClick={handleEventDropdownToggle}
                        >
                          {selectedEvent ? selectedEvent.name : "Select Event"}
                        </button>
                        <div
                          className={`dropdown-content scroll ${eventDropdownOpen ? "open" : ""}`}
                          style={{ width: "100%" }}
                        >
                          {eventNames &&
                            eventNames.map((event, index) => (
                              <p
                                key={index}
                                onClick={() => handleEventSelect(event)}
                              >
                                {event.name}
                              </p>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rewards List */}
                  {(rewardsView === "seasonal" ||
                    (rewardsView === "event" && selectedEvent)) && (
                    <div style={{ flex: 1, overflowY: "auto" }}>
                      <h3 className="title">
                        {rewardsView === "seasonal" ? (
                          "Season Rewards"
                        ) : (
                          <>
                            Event Rewards
                            <span style={{ fontFamily: "fantasy" }}>:</span>{" "}
                            {selectedEvent?.name}
                          </>
                        )}
                      </h3>

                      {(rewardsView === "seasonal"
                        ? seasonalRewards
                        : eventRewards
                      ).map((reward, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            gap: "1vw",
                            marginBottom: "0.5vw",
                            alignItems: "center",
                            alignItems: "flex-end",
                          }}
                        >
                          <div style={{ flex: "0 0 auto" }}>
                            <label className="login-label">Position:</label>
                            <input
                              type="number"
                              min="1"
                              value={reward.position}
                              onChange={(e) =>
                                handleRewardChange(
                                  rewardsView,
                                  index,
                                  "position",
                                  parseInt(e.target.value),
                                )
                              }
                              className="input-field"
                              style={{
                                width: "6vw",
                                height: "2vw",
                                margin: "0",
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="login-label">Reward:</label>
                            <input
                              type="text"
                              value={reward.reward}
                              maxLength={35}
                              onChange={(e) =>
                                handleRewardChange(
                                  rewardsView,
                                  index,
                                  "reward",
                                  e.target.value,
                                )
                              }
                              className="input-field"
                              placeholder="Enter reward description"
                              style={{ height: "2vw", margin: "0" }}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              className="default-button"
                              onClick={() =>
                                handleRemoveReward(rewardsView, index)
                              }
                              style={{
                                padding: "0.5vw",
                                height: "2vw",
                                backgroundColor: "var(--lightred)",
                                alignItems: "center",
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}

                      <div
                        style={{
                          display: "flex",
                          gap: "1vw",
                          marginTop: "1vw",
                        }}
                      >
                        <button
                          className="default-button"
                          style={{
                            marginBottom: "0.75vw",
                            marginTop: "0.75vw",
                            width: "6vw",
                            alignItems: "center",
                          }}
                          onClick={() => handleAddReward(rewardsView)}
                        >
                          Add Reward
                        </button>
                        <button
                          className="default-button submit-button"
                          onClick={() => handleSaveRewards(rewardsView)}
                          style={{ width: "88%" }}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="loader-small"></div>
                          ) : (
                            `Save ${rewardsView === "seasonal" ? "Seasonal" : "Event"} Rewards`
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="error" style={{ marginTop: "1vw" }}>
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {overlay === "DeleteLeague" && (
        <div className="overlay" onClick={handleOverlayClose}>
          <div
            className="card-menu-container"
            style={{ minWidth: "30%", minHeight: "25%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-box">
              <h1 className="title">Delete League</h1>
            </div>
            <button className="overlay-close" onClick={handleOverlayClose}>
              X
            </button>

            <div className="card-menu-content" style={{ alignItems: "center" }}>
              <button
                className="default-button big"
                style={{ height: "4vw" }}
                onClick={handleDeleteLeague}
                disabled={loading}
              >
                {" "}
                {loading ? (
                  <div className="loader-small"></div>
                ) : (
                  "Delete League"
                )}{" "}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default League;
